import { useEffect } from 'react';
import {
    CLIENT_METADATA_AVATAR_TYPE_NAME,
    MANAGER_PLACES,
    PLACES_METADATA_AVATAR_TYPE_NAME,
    SITES_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import AssignListComponent from '@/components/common/assign-list/assign-list-component';
import { useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router';
import { filter, find, map } from 'lodash';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import Box from '@mui/material/Box';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useGetManagerPlacesByUser from '@/hooks/useGetManagerPlacesByUser';
import useGetInfiniteAvatar from '@/hooks/useGetInfiniteAvatar';

interface Props {
    type:
        | typeof SITES_METADATA_AVATAR_TYPE_NAME
        | typeof CLIENT_METADATA_AVATAR_TYPE_NAME
        | typeof PLACES_METADATA_AVATAR_TYPE_NAME;
}

const AssignDialog = ({ type }: Props) => {
    const { userId = '' } = useParams();
    const navigate = useNavigate();
    const translate = useTranslate();
    const queryClient = useQueryClient();

    const handleClose = () => navigate(-1);

    const { data, isLoading, refetchAssigned } = useGetManagerPlacesByUser({ userId });

    const {
        refetch: refetchChoices,
        data: choices,
        isLoading: isLoadingChoices,
        fetchNextPage: choicesLoadMore,
        hasNextPage: choicesHasNextPage,
        isFetchingNextPage: choiceIsFetching,
    } = useGetInfiniteAvatar({
        type,
        enabled: true,
    });

    const {
        data: chosen,
        isLoading: isLoadingChosen,
        fetchNextPage: chosenLoadMore,
        hasNextPage: chosenHasNextPage,
        isFetchingNextPage: chosenIsFetching,
    } = useGetInfiniteAvatar({
        type,
        enabled: !!(data && data?.length > 0),
        filter: { id: map(data, ({ placeOrEvent }) => placeOrEvent) },
    });

    useEffect(() => {
        return () => {
            queryClient.invalidateQueries({ queryKey: [MANAGER_PLACES] });
            refetchChoices();
            refetchAssigned();
        };
    }, []);

    return (
        <DialogTemplate
            maxWidth={'md'}
            open={true}
            onClose={handleClose}
            title={translate(
                type === CLIENT_METADATA_AVATAR_TYPE_NAME
                    ? 'buttons.add_client_access'
                    : type === PLACES_METADATA_AVATAR_TYPE_NAME
                      ? 'buttons.add_place_access'
                      : 'buttons.add_site_access',
            )}
        >
            <Box display={'grid'} gap={2}>
                <AssignListComponent
                    choices={
                        !!choices
                            ? (chosen?.length ?? 0) > 0
                                ? filter(choices, ({ id }) => !find(chosen, ({ id: chosen_id }) => id === chosen_id))
                                : choices
                            : []
                    }
                    choiceHasNextPage={choicesHasNextPage ?? false}
                    choiceLoadMore={choicesLoadMore}
                    choiceIsFetching={choiceIsFetching ?? false}
                    chosen={chosen ?? []}
                    chosenHasNextPage={chosenHasNextPage ?? false}
                    chosenLoadMore={chosenLoadMore}
                    chosenIsFetching={chosenIsFetching ?? false}
                    chosenIds={data ?? []}
                    isLoading={!!(isLoading || isLoadingChoices || isLoadingChosen)}
                />
                <ButtonsBlock>
                    <Button onClick={handleClose}>{translate('ra.action.back')}</Button>
                </ButtonsBlock>
            </Box>
        </DialogTemplate>
    );
};

export default AssignDialog;
